"use client"

import { useState, useMemo } from "react"
import "./plans.css"
import PlanContactModal from "./PlanContactModal"

const SUPPORTED_LANGUAGES = ['fr', 'ar', 'en']
const DEFAULT_LANGUAGE = 'fr'

export const PLAN_CTA_LABELS = {
  fr: "Contacter le support",
  ar: "تواصل مع الدعم",
  en: "Contact support",
}

const PLAN_DEFINITIONS = [
  {
    id: "starter",
    tone: "starter",
    price: 150,
    popular: false,
    name: {
      fr: "E-Commerce Starter",
      ar: "باقة التجارة الإلكترونية",
      en: "E-Commerce Starter",
    },
    description: {
      fr: "Vendez en ligne instantanément. Aucune compétence technique requise.",
      ar: "ابدأ البيع عبر الإنترنت فوراً بدون الحاجة إلى مهارات تقنية.",
      en: "Sell online instantly. No technical skills required.",
    },
    features: {
      fr: [
        "Votre site web prêt en 5 minutes",
        "Produits illimités",
        "Tableau de bord des commandes",
        "Confirmation WhatsApp automatisée",
      ],
      ar: [
        "موقعكم الإلكتروني جاهز في 5 دقائق",
        "منتجات غير محدودة",
        "لوحة تحكم للطلبات",
        "أتمتة تأكيدات واتساب",
      ],
      en: [
        "Your website live in 5 minutes",
        "Unlimited products",
        "Orders dashboard",
        "WhatsApp confirmation automation",
      ],
    },
    ideal: {
      fr: "Idéal pour les coopératives qui démarrent leurs ventes en ligne.",
      ar: "مثالي للتعاونيات التي تبدأ البيع عبر الإنترنت.",
      en: "Ideal for small cooperatives starting online sales.",
    },
  },
  {
    id: "business",
    tone: "business",
    price: 250,
    popular: true,
    name: {
      fr: "Système de gestion",
      ar: "نظام التسيير",
      en: "Management system",
    },
    description: {
      fr: "Gérez votre coopérative comme un pro. Inclut tout E-Commerce Starter.",
      ar: "أدر تعاونيتك باحترافية. يتضمن كل ما في باقة التجارة الإلكترونية.",
      en: "Manage your cooperative professionally. Includes everything in E-Commerce Starter.",
    },
    features: {
      fr: [
        "Gestion des stocks",
        "Suivi des fournisseurs et achats",
        "Dépenses & trésorerie",
        "Rapports & insights",
      ],
      ar: [
        "إدارة المخزون",
        "تتبع المورّدين والمشتريات",
        "المصاريف والتدفق النقدي",
        "تقارير وتحليلات",
      ],
      en: [
        "Stock management",
        "Supplier & purchase tracking",
        "Expenses & cashflow",
        "Reports & insights",
      ],
    },
    ideal: {
      fr: "Idéal pour les coopératives qui veulent une organisation complète.",
      ar: "مثالي للتعاونيات التي تحتاج إلى تنظيم كامل.",
      en: "Ideal for cooperatives that want full organization.",
    },
    popularLabel: {
      fr: "Plus populaire",
      ar: "الأكثر شعبية",
      en: "Most popular",
    },
  },
  {
    id: "ecosystem",
    tone: "ecosystem",
    price: 500,
    popular: false,
    name: {
      fr: "Full Ecosystem",
      ar: "منظومة متكاملة",
      en: "Full Ecosystem",
    },
    description: {
      fr: "Toute votre coopérative, connectée.",
      ar: "كل تعاونيتك متصلة.",
      en: "Your entire cooperative, connected.",
    },
    features: {
      fr: [
        "Système point de vente",
        "Synchronisation de stock en temps réel",
        "Site web + système de gestion + point de vente unifiés",
        "Support prioritaire",
      ],
      ar: [
        "نظام نقطة البيع",
        "مزامنة مخزون فورية",
        "موقع إلكتروني + نظام تسيير + نقطة بيع موحّدة",
        "دعم أولوية",
      ],
      en: [
        "Point-of-sale system",
        "Real-time stock sync",
        "Website + management system + POS unified",
        "Priority support",
      ],
    },
    ideal: {
      fr: "Idéal pour les boutiques et coopératives professionnelles.",
      ar: "مثالي للمتاجر والتعاونيات الاحترافية.",
      en: "Ideal for boutiques & professional cooperatives.",
    },
  },
]

const resolveLanguage = (language = DEFAULT_LANGUAGE) => (
  SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE
)

export const getPricingPlans = (language = DEFAULT_LANGUAGE) => {
  const langKey = resolveLanguage(language)

  return PLAN_DEFINITIONS.map((plan) => ({
    id: plan.id,
    tone: plan.tone,
    price: plan.price,
    popular: plan.popular,
    popularLabel: plan.popularLabel ? plan.popularLabel[langKey] : null,
    name: plan.name[langKey] || plan.name.en,
    description: plan.description[langKey] || plan.description.en,
    features: plan.features[langKey] || plan.features.en,
    note: plan.note ? plan.note[langKey] || plan.note.en : null,
    ideal: plan.ideal ? plan.ideal[langKey] || plan.ideal.en : null,
  }))
}

const Plans = ({ language = DEFAULT_LANGUAGE }) => {
  const langKey = resolveLanguage(language)
  const [contactPlan, setContactPlan] = useState(null)
  const [isAnnual, setIsAnnual] = useState(false)

  const plans = useMemo(() => getPricingPlans(langKey), [langKey])

  const getCurrentPrice = (plan) => {
    if (isAnnual) {
      return plan.price * 10 // Annual = 10 months (2 months free)
    }
    return plan.price
  }

  return (
    <div className="plans-container" id="pricing">
      {/* Toggle Section */}
      <div className="billing-toggle">
        {isAnnual && <div className="annual-badge">{langKey === 'fr' ? 'Paiement annuel' : langKey === 'ar' ? 'دفع سنوي' : 'Annual payment'}</div>}
        <div className="toggle-buttons">
          <button
            className={`toggle-btn ${!isAnnual ? "active" : ""}`}
            onClick={() => setIsAnnual(false)}
          >
            {langKey === 'fr' ? 'Mensuel' : langKey === 'ar' ? 'شهري' : 'Monthly'}
          </button>
          <button
            className={`toggle-btn ${isAnnual ? "active" : ""}`}
            onClick={() => setIsAnnual(true)}
          >
            {langKey === 'fr' ? 'Annuel' : langKey === 'ar' ? 'سنوي' : 'Annual'}
          </button>
        </div>
      </div>

      <div className="plans-grid">
        {plans.map((plan) => (
          <div key={plan.id} className={`plan-card plan-${plan.tone} ${plan.popular ? 'popular' : ''}`}>
            {plan.popular && (
              <div className="popular-badge">
                {plan.popularLabel}
              </div>
            )}

            <div className="plan-header">
              <h3 className="plan-name">{plan.name}</h3>
              <div className="plan-price">
                <span className="price-amount">{getCurrentPrice(plan)}</span>
                <span className="price-currency">DH</span>
                <span className="price-period">/ {isAnnual ? (langKey === 'fr' ? 'an' : langKey === 'ar' ? 'سنة' : 'year') : (langKey === 'fr' ? 'mois' : langKey === 'ar' ? 'شهر' : 'month')}</span>
              </div>
              {isAnnual && (
                <div className="annual-note">
                  {langKey === 'fr' ? 'Un paiement annuel' : langKey === 'ar' ? 'دفع سنوي واحد' : 'One annual payment'}
                </div>
              )}
              <p className="plan-description">{plan.description}</p>
            </div>

            <div className="plan-features">
              <ul className="features-list">
                {plan.features.map((feature, index) => (
                  <li key={`${plan.id}-feature-${index}`} className="feature-item">
                    <svg className="feature-check" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              {plan.ideal && <div className="plan-ideal">{plan.ideal}</div>}
            </div>

            <div className="plan-footer">
              <button 
                className={`plan-button ${plan.id === "starter" ? "btn-solo" : plan.id === "business" ? "btn-solo" : "btn-multi"}`} 
                onClick={() => setContactPlan(plan)}
              >
                {PLAN_CTA_LABELS[langKey]}
              </button>
            </div>
          </div>
        ))}
      </div>

      {contactPlan && (
        <PlanContactModal
          plan={contactPlan}
          language={langKey}
          onClose={() => setContactPlan(null)}
        />
      )}
    </div>
  )
}

export { resolveLanguage }

export default Plans
